import { getDisplayMode } from "./displayMode"

/**
 * App icon badge (Badging API) of the installed PWA.
 *
 * Set only when the unread count changes. Repainting it more often (on
 * focus, on every message, on a timer) was tried in #693 to fight Chrome on
 * Windows overwriting the badge with its profile avatar, but every call
 * surfaced as an empty notification for some users, so it was rolled back.
 */
export type AppBadgeReason = "change"

export type AppBadgeResult = "ok" | "error" | "unsupported"

type BadgingNavigator = Navigator & {
  setAppBadge?: (contents?: number) => Promise<void>
  clearAppBadge?: () => Promise<void>
}

const badgingNavigator = (): BadgingNavigator | undefined =>
  typeof navigator === "undefined" ? undefined : (navigator as BadgingNavigator)

export const isAppBadgeSupported = (): boolean =>
  typeof badgingNavigator()?.setAppBadge === "function"

export const APP_BADGE_TRACK_EVENT = "Badge.Set"

const describeError = (error: unknown): string | undefined => {
  if (error === undefined || error === null) return undefined
  if (error instanceof Error) return `${error.name}: ${error.message}`
  return String(error)
}

const track = (
  count: number,
  reason: AppBadgeReason,
  result: AppBadgeResult,
  error?: unknown,
): void => {
  window.analytics?.track?.(APP_BADGE_TRACK_EVENT, {
    count,
    reason,
    result,
    supported: isAppBadgeSupported(),
    displayMode: getDisplayMode(),
    error: describeError(error),
  })
}

/**
 * Sets (count > 0) or clears (count <= 0) the app icon badge. Never throws;
 * the outcome is reported to analytics instead.
 */
export const applyAppBadge = async (
  count: number,
  reason: AppBadgeReason = "change",
): Promise<AppBadgeResult> => {
  const nav = badgingNavigator()
  if (!nav || typeof nav.setAppBadge !== "function") {
    track(count, reason, "unsupported")
    return "unsupported"
  }

  try {
    if (count > 0) {
      await nav.setAppBadge(count)
    } else if (typeof nav.clearAppBadge === "function") {
      await nav.clearAppBadge()
    } else {
      await nav.setAppBadge(0)
    }
    track(count, reason, "ok")
    return "ok"
  } catch (error) {
    track(count, reason, "error", error)
    return "error"
  }
}
